import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import '@/styles/hacker-aesthetic.css';
import '@/styles/settings.css';

interface SettingsPanelProps {
  onClose: () => void;
}

interface UserSettings {
  soundEnabled: boolean;
  particleEffectsEnabled: boolean;
  notificationsEnabled: boolean;
  language: 'en' | 'de';
  networkNotifications: boolean;
  analyticsRefresh: number;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState<UserSettings>({
    soundEnabled: true,
    particleEffectsEnabled: true,
    notificationsEnabled: true,
    language: 'en',
    networkNotifications: true,
    analyticsRefresh: 30,
  });

  useEffect(() => {
    const savedSettings = localStorage.getItem('nodeCommander_settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(parsed);
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    }
  }, []);

  const saveSettings = (newSettings: UserSettings) => {
    setSettings(newSettings);
    localStorage.setItem('nodeCommander_settings', JSON.stringify(newSettings));
    window.dispatchEvent(new CustomEvent('nodeCommander_settings', { detail: newSettings }));

    if (newSettings.language !== i18n.language) {
      i18n.changeLanguage(newSettings.language);
    }

    toast.success(t('messages.saved'));
  };

  const toggleSetting = (key: keyof UserSettings) => {
    saveSettings({
      ...settings,
      [key]: !settings[key],
    });
  };

  const renderSwitch = (key: 'soundEnabled' | 'particleEffectsEnabled' | 'notificationsEnabled' | 'networkNotifications') => (
    <div className="set-control">
      <button
        type="button"
        role="switch"
        aria-checked={settings[key]}
        className={`set-switch ${settings[key] ? 'active' : ''}`}
        onClick={() => toggleSetting(key)}
      >
        <span className="set-switch-knob" />
      </button>
      <span className={`set-switch-state ${settings[key] ? 'on' : ''}`}>
        {settings[key] ? t('settings.enabled') : t('settings.disabled')}
      </span>
    </div>
  );

  return (
    <div className="set-panel">
      <div className="set-header">
        <div className="set-heading">
          <div className="set-title">{t('settings.title')}</div>
          <span className="set-chip">{t('settings.config')}</span>
        </div>
        <button className="set-close" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      <div className="set-body">
        <div className="set-section">
          <div className="set-section-title">{t('settings.general')}</div>
          <div className="set-grid">
            <div className="set-item">
              <span className="set-label">{t('settings.sound')}</span>
              {renderSwitch('soundEnabled')}
            </div>
            <div className="set-item">
              <span className="set-label">{t('settings.particles')}</span>
              {renderSwitch('particleEffectsEnabled')}
            </div>
            <div className="set-item">
              <span className="set-label">{t('settings.notifications')}</span>
              {renderSwitch('notificationsEnabled')}
            </div>
          </div>
        </div>

        <div className="set-section">
          <div className="set-section-title">{t('settings.language')}</div>
          <div className="set-grid">
            <div className="set-item">
              <span className="set-label">{t('settings.language_select')}</span>
              <div className="set-control">
                <select
                  value={settings.language}
                  onChange={(e) => saveSettings({ ...settings, language: e.target.value as UserSettings['language'] })}
                  className="set-select"
                >
                  <option value="en">English</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="set-section">
          <div className="set-section-title">{t('settings.advanced')}</div>
          <div className="set-grid">
            <div className="set-item">
              <span className="set-label">{t('settings.network_notifications')}</span>
              {renderSwitch('networkNotifications')}
            </div>
            <div className="set-item">
              <span className="set-label">{t('settings.analytics_refresh')}</span>
              <div className="set-control">
                <span className="set-hint">{t('settings.refresh_every')}</span>
                <select
                  value={settings.analyticsRefresh}
                  onChange={(e) => saveSettings({ ...settings, analyticsRefresh: parseInt(e.target.value) })}
                  className="set-select"
                >
                  <option value="10">10s</option>
                  <option value="30">30s</option>
                  <option value="60">1m</option>
                  <option value="300">5m</option>
                </select>
                <span className="set-hint">{t('settings.seconds')}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="set-section">
          <div className="set-section-title">{t('settings.account')}</div>
          <div className="set-grid">
            <div className="set-item">
              <span className="set-label">{t('settings.export_data')}</span>
              <div className="set-control">
                <button
                  className="set-button"
                  onClick={() => {
                    const data = { settings, timestamp: new Date().toISOString(), version: '2.0.2' };
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `nodeCommander-settings-${Date.now()}.json`;
                    a.click();
                    toast.success(t('messages.exported'));
                  }}
                >
                  {t('actions.export')}
                </button>
              </div>
            </div>
            <div className="set-item">
              <span className="set-label">{t('settings.reset_settings')}</span>
              <div className="set-control">
                <button
                  className="set-button danger"
                  onClick={() => {
                    const defaultSettings: UserSettings = {
                      soundEnabled: true,
                      particleEffectsEnabled: true,
                      notificationsEnabled: true,
                      language: 'en',
                      networkNotifications: true,
                      analyticsRefresh: 30,
                    };
                    saveSettings(defaultSettings);
                    toast.success(t('messages.reset'));
                  }}
                >
                  {t('actions.reset')}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="set-status">
          <div className="set-status-item">
            <span className="set-status-label">{t('settings.sound')}</span>
            <span className={`set-status-value ${settings.soundEnabled ? 'active' : 'inactive'}`}>
              {settings.soundEnabled ? t('settings.on') : t('settings.off')}
            </span>
          </div>
          <div className="set-status-item">
            <span className="set-status-label">{t('settings.particles')}</span>
            <span className={`set-status-value ${settings.particleEffectsEnabled ? 'active' : 'inactive'}`}>
              {settings.particleEffectsEnabled ? t('settings.on') : t('settings.off')}
            </span>
          </div>
          <div className="set-status-item">
            <span className="set-status-label">{t('settings.notifications')}</span>
            <span className={`set-status-value ${settings.notificationsEnabled ? 'active' : 'inactive'}`}>
              {settings.notificationsEnabled ? t('settings.on') : t('settings.off')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
